import React, { useState, useEffect, useCallback } from 'react';
import { walletApi } from '../services/api';
import { WalletBalance, WalletTransaction, WalletTransactionFilters } from '../types';
import Pagination from '../components/Pagination';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  ArrowDownward as DebitIcon,
  ArrowUpward as TopupIcon,
  FilterList as FilterIcon,
  ErrorOutline as ErrorIcon,
  ReceiptLong as ReceiptIcon,
} from '@mui/icons-material';

const Wallet: React.FC = () => {
  console.log('Wallet component loaded - NEW UI VERSION');
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<WalletTransactionFilters>({
    page: 1,
    limit: 10,
    transaction_type: undefined,
    date_from: '',
    date_to: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  const loadWalletData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load balance and transactions in parallel
      const [balanceResponse, transactionsResponse] = await Promise.all([
        walletApi.getBalance(),
        walletApi.getTransactions(filters)
      ]);

      if (balanceResponse.status === 'success' && balanceResponse.data) {
        setBalance(balanceResponse.data);
      }

      if (transactionsResponse.status === 'success' && transactionsResponse.data) {
        // Ensure data is an array
        const transactionsData = Array.isArray(transactionsResponse.data) 
          ? transactionsResponse.data 
          : [];
        setTransactions(transactionsData);
        if (transactionsResponse.pagination) {
          setPagination(transactionsResponse.pagination);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadWalletData();
  }, [loadWalletData]);

  const handleFilterChange = (key: keyof WalletTransactionFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setFilters(prev => ({
      ...prev,
      limit: pageSize,
      page: 1 // Reset to first page when page size changes
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 10,
      transaction_type: undefined,
      date_from: '',
      date_to: ''
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionChip = (type: string) => (
    <Chip
      icon={type === 'debit' ? <DebitIcon fontSize="small" /> : <TopupIcon fontSize="small" />}
      label={type.toUpperCase()}
      color={type === 'debit' ? 'error' : 'success'}
      size="small"
      variant="outlined"
    />
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="primary" />
          <Typography color="text.secondary">Loading wallet data...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>

        {/* Balance */}
        {balance && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="body2" color="text.secondary">Wallet Balance</Typography>
                <Typography variant="h5" fontWeight={700}>{balance.balance} {balance.currency}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <FilterIcon color="action" />
            <Typography variant="h6">Transaction History</Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="txn-type-label">Transaction Type</InputLabel>
                <Select
                  labelId="txn-type-label"
                  label="Transaction Type"
                  value={filters.transaction_type || ''}
                  onChange={(e) => handleFilterChange('transaction_type', e.target.value || undefined)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="debit">Debit</MenuItem>
                  <MenuItem value="topup">Topup</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date From"
                InputLabelProps={{ shrink: true }}
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date To"
                InputLabelProps={{ shrink: true }}
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth variant="outlined" color="inherit" sx={{ height: '40px' }} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper>
          <Box sx={{ p: 2 }}>
            {error && (
              <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!error && transactions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="h6" sx={{ mt: 1 }}>No transactions found</Typography>
                <Typography color="text.secondary">Your transaction history will appear here once you start creating orders or receive credits.</Typography>
              </Box>
            ) : !error && (
              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Balance After</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(transactions) && transactions.map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell>{getTransactionChip(t.transaction_type)}</TableCell>
                        <TableCell>
                          <Typography color={t.transaction_type === 'debit' ? 'error.main' : 'success.main'} fontWeight={700}>
                            {t.transaction_type === 'debit' ? '-' : '+'}{t.amount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>{t.balance_after}</Typography>
                        </TableCell>
                        <TableCell>{t.description}</TableCell>
                        <TableCell>{t.customer_name || '-'}</TableCell>
                        <TableCell>
                          {t.order_number ? (
                            <Chip size="small" label={t.order_number} color="primary" variant="outlined" />
                          ) : '-'}
                        </TableCell>
                        <TableCell>{formatDate(t.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>

          {/* Pagination */}
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </Paper>
    </Box>
  );
};

export default Wallet;
